import React, { JSX, useMemo, useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Paper } from '@mui/material';
import { getCoreRowModel } from '@tanstack/react-table';

// Components
import { useStandardTable as useTable, Table } from '../../common/table/index';
import CustomModal from '../../common/withModal';

// Service
import { getVisitLog } from '../../../services/visit.service';

// Columns
import { makeClosestReaderLogColumns } from '../../../utils/columns/makeClosestReaderLogColumns';
import { dateHeader, formatCellDateWithSeconds } from '../../../utils/columns/timeModeCell';

// Types
import { ClosestReaderLog } from '../../../types/ClosestReaderLog';
import { VisitReaderLog } from '../../../types/VisitReader';

const makeVisitReaderLogColumns = () => [
  {
    accessorKey: 'imei',
    header: 'IMEI',
  },
  {
    accessorKey: 'timestamp',
    header: dateHeader('Timestamp'),
    cell: ({ getValue, table }: any) => formatCellDateWithSeconds(table, getValue()),
  },
  {
    accessorKey: 'lastSeenDateTime',
    header: dateHeader('Last Seen'),
    cell: ({ getValue, table }: any) => formatCellDateWithSeconds(table, getValue()),
  },
  {
    accessorKey: 'name',
    header: 'Name',
  },
  {
    accessorKey: 'facilityName',
    header: 'Facility Name',
  },
];

export default function VisitHistoryDetail(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const [paramFilter, setParamFilter] = useState<any>({
    sortBy: [{ createdDateTime: '-1' }],
    filters: id ? [{ DeviceId: id }] : [],
  });
  const [closestReaderLog, setClosestReaderLog] = useState<VisitReaderLog[] | null>(null);

  useEffect(() => {
    if (id) {
      setParamFilter((prev: any) => ({
        ...prev,
        filters: [{ DeviceId: id }],
        sortBy: prev?.sortBy ?? [
          {
            createdDateTime: '-1',
          },
        ],
      }));
    }
  }, [id]);

  const handleClosestReaderLog = (value: VisitReaderLog[]): void => {
    setClosestReaderLog(value);
  };

  const closestReaderColumns = useMemo(() => makeClosestReaderLogColumns(handleClosestReaderLog), []);

  const visitReaderLogColumns = useMemo(() => makeVisitReaderLogColumns(), []);

  const closestReaderTable = useTable<ClosestReaderLog>({
    columns: closestReaderColumns,
    fetchAsync: getVisitLog,
    params: paramFilter,
    serverSide: true,
    shouldFetch: true,
  });

  const visitReaderLogTable = useTable<VisitReaderLog>({
    columns: visitReaderLogColumns,
    fetchAsync: async () => {
      return {
        status: 200,
        data: {
          results: closestReaderLog || [],
          totalCount: closestReaderLog?.length || 0,
        },
      };
    },
    serverSide: false,
    shouldFetch: !!closestReaderLog,
  });

  return (
    <>
      <CustomModal title='Closest Reader Log' isOpen={!!closestReaderLog} setIsOpen={() => setClosestReaderLog(null)} maxWidth='lg'>
        {closestReaderLog && (
          <Table {...visitReaderLogTable} showSearchBar={false} showColumnToggler={false} showAction={false} getCoreRowModel={getCoreRowModel()} />
        )}
      </CustomModal>

      <Paper>
        <h4 style={{ margin: '10px', paddingTop: '6px' }}>All Closest Reader Details</h4>
        <Table {...closestReaderTable} showSearchBar={false} showColumnToggler={false} showAction={false} getCoreRowModel={getCoreRowModel()} />
      </Paper>
    </>
  );
}
